import React, { useState, useEffect } from "react";
import {
  getItemId,
  getPlatformConfig,
  getWorkspaceId,
  getWorkspaceName,
} from "../AssistantDetails/Assistants.utils";
import { formatPlatformName, getIcon } from "../../constants/constants";
import {
  DEFAULT_SETTINGS_STEP,
  PLATFORM_OPTIONS,
  STRIP_SELECT_RE,
} from "./PlatformWorkspace.utils";
import LoadingComponent from "../../components/common/LoadingComponent";
import {
  OptionList,
  Stepper,
  DefaultSettingsSection,
} from "./WorkspaceProject.utils";

function WorkspaceProject({
  platform,
  workspaces,
  projects = [],
  items,
  selectedWorkspace,
  selectedProject,
  selectedItem,
  handleWorkspaceChange,
  handleProjectChange,
  setSelectedItem,
  handleContinue,
  handleSkip,
  loading,
  projectsLoading,
  setting,
  advancedSettings,
  advancedSettingsLoading,
  selectedDefaultSettings,
  setSelectedDefaultSettings,
  isUpdateMode,
}) {
  const platformCfg = PLATFORM_OPTIONS[platform] || {
    preSteps: [],
    settingsLoad: "onLoad",
    autoAdvance: true,
    isDatabaseOnly: false,
  };

  const { preSteps, isDatabaseOnly = false } = platformCfg;
  const allSteps = [...preSteps, DEFAULT_SETTINGS_STEP];
  const hasBoardStep = preSteps.some((s) => s.key === "board");
  const defaultSettingsIdx = allSteps.findIndex((s) => s.key === "defaultSettings");

  const [currentStep, setCurrentStep] = useState(0);
  const [localSelectedDatabase, setLocalSelectedDatabase] = useState("");

  useEffect(() => {
    setCurrentStep(0);
  }, [platform]);

  useEffect(() => {
    if (isDatabaseOnly && selectedItem) {
      setLocalSelectedDatabase(String(selectedItem));
    }
  }, [isDatabaseOnly, selectedItem]);

  const config = getPlatformConfig(platform);
  const isLastStep = currentStep === allSteps.length - 1;
  const canGoBack = currentStep > 0;
  const currentStepKey = allSteps[currentStep]?.key;
  const requiredField = advancedSettings?.requiredField;

  const workspaceOptions = workspaces.map((w) => ({
    id: getWorkspaceId(w, platform),
    label: getWorkspaceName(w, platform),
  }));

  const projectOptions = projects.map((p) => ({
    id: String(p.id),
    label: p.name,
  }));

  const itemOptions = items.map((item) => ({
    id: String(
      getItemId(item, platform) || item.id || item.gid || item.database_id,
    ),
    label: item.name,
  }));

  const isNextDisabled = (() => {
    if (isUpdateMode) {
      if (!isLastStep) return false;
      if (!requiredField) return false;
      const value = selectedDefaultSettings?.[requiredField];
      return Array.isArray(value) ? value.length === 0 : !value?.key;
    }

    switch (currentStepKey) {
      case "workspace":
        return !selectedWorkspace;
      case "project":
        return !selectedProject;
      case "board":
        return items.length > 0 && !selectedItem;
      case "item":
        return !selectedItem;
      case "database":
        return !localSelectedDatabase;
      case "defaultSettings": {
        if (!requiredField) return false;
        const value = selectedDefaultSettings?.[requiredField];
        return Array.isArray(value) ? value.length === 0 : !value?.key;
      }
      default:
        return false;
    }
  })();

  const handleBack = () => {
    if (hasBoardStep && currentStep === defaultSettingsIdx) {
      const boardWasSkipped =
        selectedItem === "NO_BOARD" || items.length === 0;
      if (boardWasSkipped) {
        setCurrentStep(allSteps.findIndex((s) => s.key === "project"));
        return;
      }
    }
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const handleNext = async () => {
    if (currentStepKey === "database" && localSelectedDatabase) {
      setCurrentStep((s) => s + 1);
      await setSelectedItem(String(localSelectedDatabase), true);
      return;
    }
    if (isLastStep) {
      handleContinue();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const onWorkspaceSelect = (id) => {
    handleWorkspaceChange(id);
    setCurrentStep((s) => s + 1);
  };

  const onProjectSelect = async (id) => {
    const hasBoards = await handleProjectChange(id);
    if (hasBoardStep && !hasBoards) {
      setCurrentStep(defaultSettingsIdx);
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const onItemSelect = (id) => {
    setSelectedItem(String(id), false);
    setCurrentStep((s) => s + 1);
  };

  const onBoardSelect = (id) => {
    setSelectedItem(String(id), false);
    setCurrentStep((s) => s + 1);
  };

  const onDatabaseSelect = (id) => {
    setLocalSelectedDatabase(String(id));
  };

  const stripSelectPrefix = (label, fallback) =>
    (label || fallback).replace(STRIP_SELECT_RE, "");

  const stepHeading = (() => {
    if (isLastStep) {
      return `Select ${formatPlatformName(platform)} Default Settings`;
    }
    const step = allSteps[currentStep];
    return isUpdateMode
      ? `Selected ${stripSelectPrefix(step.label, "Item")}`
      : step.label;
  })();

  const stepperSteps = allSteps.map((s) => ({ label: s.label }));

  let iconSrc = getIcon(platform);
  if (iconSrc === undefined) iconSrc = platform?.iconUrl;

  const renderStepContent = () => {
    switch (currentStepKey) {
      case "workspace":
        return loading ? (
          <LoadingComponent loading={true} />
        ) : (
          <OptionList
            options={workspaceOptions}
            selectedId={selectedWorkspace}
            onSelect={isUpdateMode ? undefined : onWorkspaceSelect}
            disabled={isUpdateMode}
          />
        );

      case "project":
        return projectsLoading ? (
          <LoadingComponent loading={true} />
        ) : (
          <OptionList
            options={projectOptions}
            selectedId={selectedProject}
            onSelect={isUpdateMode ? undefined : onProjectSelect}
            disabled={isUpdateMode}
          />
        );

      case "board":
        return projectsLoading ? (
          <LoadingComponent loading={true} />
        ) : (
          <OptionList
            options={itemOptions}
            selectedId={selectedItem}
            onSelect={isUpdateMode ? undefined : onBoardSelect}
            disabled={isUpdateMode}
          />
        );

      case "item":
        return projectsLoading ? (
          <LoadingComponent loading={true} />
        ) : (
          <OptionList
            options={itemOptions}
            selectedId={selectedItem}
            onSelect={isUpdateMode ? undefined : onItemSelect}
            disabled={isUpdateMode}
          />
        );

      case "database":
        return loading ? (
          <LoadingComponent loading={true} />
        ) : (
          <OptionList
            options={itemOptions}
            selectedId={String(localSelectedDatabase)}
            onSelect={onDatabaseSelect}
            emptyMessage="No databases found."
            disabled={isUpdateMode}
          />
        );

      default:
        return (
          <DefaultSettingsSection
            platform={platform}
            advancedSettings={advancedSettings}
            loading={advancedSettingsLoading}
            selectedDefaultSettings={selectedDefaultSettings}
            setSelectedDefaultSettings={setSelectedDefaultSettings}
          />
        );
    }
  };

  return (
    <div className="container-fluid px-4 mt-10">
      <div className="card">
        <div className="ws-card-header px-4 py-3 d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center bg-body-emphasis"
            style={{ width: 50, height: 50 }}
          >
            <img
              alt={platform}
              src={iconSrc}
              style={{ width: 22, height: 22 }}
            />
          </div>
          <div>
            <p className="ws-step-heading mb-1">Integration Setup</p>
            <h5 className="mb-0 fw-bold">{config.title}</h5>
          </div>
        </div>

        {allSteps.length > 1 && (
          <Stepper steps={stepperSteps} currentStep={currentStep} />
        )}

        {setting?.accessToken && (
          <div className="px-4 py-4 ws-panel">
            <p className="ws-label mb-2">{stepHeading}</p>
            {renderStepContent()}
          </div>
        )}

        <div className="ws-card-footer px-4 py-3 d-flex justify-content-end gap-2">
          {canGoBack && (
            <button className="btn btn-outline-secondary" onClick={handleBack}>
              ← Back
            </button>
          )}
          <button
            className="btn btn-primary"
            disabled={isNextDisabled}
            onClick={handleNext}
          >
            {isLastStep ? "Save & Continue" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WorkspaceProject;